import React, { useState } from "react";
import { Scrollama, Step } from "react-scrollama";
import { scaleOrdinal } from "d3-scale";
import { range, group } from "d3-array";
import { format } from "d3-format";
import { rgb } from "d3-color";
import BiggestSenders from "./../BiggestSenders";
import SelectedCountry from "./../SelectedCountry";
import ReceivingCountries from "./../ReceivingCountries";
import flowmapData from "../../data/data.json";
import ContainerDimensions from "react-container-dimensions";

const Scroller = ({ props }) => {
  const { id, scenes, title, source, data, width, height } = props;

  const [selection, setSelection] = useState(undefined);

  const createMarkup = (text) => {
    return { __html: text };
  };

  const margin = {
    top: 120,
    right: 20,
    bottom: 20,
    left: 20,
  };

  const innerWidth = props.width - margin.left - margin.right;

  const f = format(".2s");

  const [interactive, setInteractive] = useState(false);
  const [sceneIndex, setSceneIndex] = useState(0);

  const handleStepEnter = ({ element, data }) => {
    setSelection(
      data.selected === ""
        ? undefined
        : countries.find((d) => d.code === data.selected)
    );
    setInteractive(data.interactive === "true");
    setSceneIndex(+data.index);
  };

  const cr = scaleOrdinal()
    .domain(["ECS", "MEA", "SSF", "NAC", "SAS", "EAS", "LCN"])
    .range([
      "#D93C39",
      "#9451a2",
      "#E7D22D",
      "#878787",
      "#54B3C2",
      "#E8B075",
      "#7ABF8B",
    ]);

  const regions = flowmapData.regions.map((d) => ({
    ...d,
    color: cr(d.code),
  }));

  function toTimeSeries(obj) {
    const values = Object.keys(obj)
      .filter((k) => obj[k] === "" || !isNaN(obj[k]))
      .map((k, i) => obj[k]);

    return values.length === 0
      ? []
      : range(1980, 2020).map((year, i) => ({
          year,
          value: values[i] === "" ? undefined : values[i],
        }));
  }

  function createExtendedCountry(country) {
    const share = flowmapData.share_gdp.find(
      (d) => d.country_code === country.code
    );

    return {
      ...country,
      sendingTo: [],
      receivingFrom: [],
      amount: 0,
      region: regions.find((d) => d.code === country.region),
      income_group: flowmapData.income_groups.find(
        (d) => d.code === country.income_group
      ),
      inflow: toTimeSeries(
        flowmapData.inflow.find((d) => d.country_code === country.code)
      ),
      outflow: toTimeSeries(
        flowmapData.outflow.find((d) => d.country_code === country.code)
      ),
      inflow_share_gdp:
        share.inflow_share_2019 === "" ? undefined : share.inflow_share_2019,
      outflow_share_gdp:
        share.outflow_share_2018 === "" ? undefined : share.outflow_share_2018,
      sendingTotalAmount() {
        return this.sendingTo.reduce((a, b) => a + b.amount, 0);
      },
      receivingTotalAmount() {
        return this.receivingFrom.reduce((a, b) => a + b.amount, 0);
      },
      sendingToSelectedCountry() {
        return selection
          ? this.sendingTo
              .filter((d) => d.code === selection.code)
              .reduce((a, b) => a + b.amount, 0)
          : 0;
      },
      isSending() {
        return this.sendingTo.length > 0;
      },
      isReceiving() {
        return this.receivingFrom.length > 0;
      },
    };
  }

  const countries = flowmapData.bilateral_remittances_NEW.reduce((acc, cur) => {
    let sender = acc.find((d) => d.code === cur.sending);
    let receiver = acc.find((d) => d.code === cur.receiving);

    if (!sender) {
      const country = flowmapData.countries.find((d) => d.code === cur.sending);

      if (country) {
        sender = createExtendedCountry(country);
        acc.push(sender);
      } else {
        console.error("sending country not found", cur);
      }
    }

    if (!receiver) {
      const country = flowmapData.countries.find(
        (d) => d.code === cur.receiving
      );

      if (country) {
        receiver = createExtendedCountry(country);
        acc.push(receiver);
      } else {
        console.error("receiving country not found", cur);
      }
    }

    if (sender && receiver) {
      sender.sendingTo.push({ ...receiver, amount: cur.amount });
      receiver.receivingFrom.push({ ...sender, amount: cur.amount });
    }

    return acc;
  }, []);

  const note = props.note ? (
    <p
      className="chart-note"
      dangerouslySetInnerHTML={createMarkup(props.note)}
    ></p>
  ) : (
    ""
  );

  return (
    <div className="scroller" id={id}>
      <div className="scroll-graphic">
        <div className="chart-wrapper ">
          <p className="chart-title">{title}</p>
          <div className="chart">
            <ContainerDimensions>
              {({ width, height }) => (
                <div style={{ textAlign: "center" }}>
                  <svg
                    height={height}
                    width={width}
                    viewBox={`0 0 ${width} ${height}`}
                  >
                    <defs>
                      <pattern
                        id="pattern-diagonal"
                        width="6"
                        height="6"
                        patternUnits="userSpaceOnUse"
                      >
                        <line x2={8} y2={8} stroke="#bababa" />
                      </pattern>
                    </defs>
                    {!selection && sceneIndex > 0 && (
                      <g>
                        <text
                          x={width / 2}
                          y={100}
                          style={{ fontSize: 24, textAnchor: "middle" }}
                        >
                          {props.tryForYourself}
                        </text>
                        <text
                          x={width / 2}
                          y={100}
                          dy={20}
                          style={{
                            fontSize: 14,
                            textAnchor: "middle",
                            fontStyle: "italic",
                          }}
                        >
                          {props.selectACountryBelow}
                        </text>
                      </g>
                    )}
                    <g
                      transform={`translate(${margin.left}, ${
                        height - (height >= 800 ? 600 : 445)
                      })`}
                    >
                      {selection && (
                        <g>
                          <SelectedCountry
                            selection={selection}
                            width={width - margin.left - margin.right}
                            {...props}
                          />
                          <BiggestSenders
                            selection={selection}
                            f={f}
                            countries={countries}
                            width={width - margin.left - margin.right}
                            {...props}
                          />
                        </g>
                      )}
                      {/* <ReceivingCountries
                        selection={selection}
                        countries={countries}
                        setSelection={setSelection}
                        interactive={sceneIndex === 5}
                        width={width - margin.left - margin.right}
                      /> */}
                    </g>
                  </svg>
                  {sceneIndex >= 0 && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 45,
                        height: 240,
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      <p style={{ fontSize: 11, margin: 0 }}>
                        {props.economiesOnePercent}
                      </p>
                      <p
                        style={{
                          width: "100%",
                          textAlign: "center",
                          fontSize: 14,
                          fontStyle: "italic",
                          textAlign: "center",
                          fontWeight: "bold",
                          margin: "5px 0px",
                          height: "19px",
                        }}
                      >
                        {interactive ? props.swipeLeftRight : ""}
                      </p>
                      {/* <p
                        style={{
                          width: '100vw',
                          textAlign: 'center',
                          fontSize: 14,
                          fontStyle: 'italic',
                          margin: '5px 0 5px 0'
                        }}
                      >
                        Scroll left and right to see more countries
                      </p> */}
                      <div
                        style={{
                          backgroundColor: "#ECEFF1",

                          height: 240,
                          display: "grid",
                          gridTemplateColumns: "120px 1fr",
                        }}
                      >
                        <div>
                          {Array.from(
                            group(
                              countries.filter((d) => d.inflow_share_gdp > 1),
                              (d) => d.region.code
                            )
                          )
                            .sort((a, b) => b[1].length - a[1].length)
                            .map((region, j) => (
                              <div
                                key={`label-${j}`}
                                style={{
                                  height: 40,
                                  fontSize: 13,
                                  paddingRight: 10,
                                  fontWeight: "bold",
                                  borderBottom: "1px solid white",
                                  borderLeft: `7px solid rgb(${
                                    rgb(cr(region[1][0].region.code)).r
                                  }, ${rgb(cr(region[1][0].region.code)).g}, ${
                                    rgb(cr(region[1][0].region.code)).b
                                  })`,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  paddingRight: 10,
                                }}
                              >
                                <div
                                  style={{
                                    textAlign: "right",
                                    lineHeight: "14px",
                                  }}
                                >
                                  {region[1][0].region.label}
                                </div>
                              </div>
                            ))}
                        </div>
                        <div style={{ overflowX: "scroll" }}>
                          {Array.from(
                            group(
                              countries.filter((d) => d.inflow_share_gdp > 1),
                              (d) => d.region.code
                            )
                          )
                            .sort((a, b) => b[1].length - a[1].length)
                            .map((region, j) => (
                              <div
                                key={`region-${j}`}
                                style={{
                                  width: 30 * 101,
                                  height: 40,
                                  borderBottom: "1px solid white",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {region[1]
                                  .sort(
                                    (a, b) =>
                                      b.inflow_share_gdp - a.inflow_share_gdp
                                  )
                                  .map((country) => (
                                    <button
                                      key={country.code}
                                      style={{
                                        fontSize: 12,
                                        width: 100,
                                        height: 37,
                                        outline: "none",
                                        color:
                                          selection &&
                                          selection.code === country.code
                                            ? "white"
                                            : interactive
                                            ? "black"
                                            : "#999",
                                        borderRadius: 2,
                                        lineHeight: "11px",
                                        border: "none",
                                        // border: interactive
                                        //   ? `0.5px solid rgb(${
                                        //       rgb(cr(country.region.code)).r
                                        //     }, ${
                                        //       rgb(cr(country.region.code)).g
                                        //     }, ${
                                        //       rgb(cr(country.region.code)).b
                                        //     })`
                                        //   : 'none',
                                        marginRight: 1,
                                        backgroundColor:
                                          selection &&
                                          selection.code === country.code
                                            ? "#263238"
                                            : interactive
                                            ? `rgba(${
                                                rgb(cr(country.region.code)).r
                                              }, ${
                                                rgb(cr(country.region.code)).g
                                              }, ${
                                                rgb(cr(country.region.code)).b
                                              }, ${
                                                (country.inflow_share_gdp /
                                                  100) *
                                                5
                                              })`
                                            : "#eaeaea",
                                        cursor: interactive
                                          ? "pointer"
                                          : "default",
                                      }}
                                      onClick={() => {
                                        if (interactive) {
                                          setSelection(country);
                                        }
                                      }}
                                    >
                                      {country.label}
                                    </button>
                                  ))}
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ContainerDimensions>
          </div>
          {note}
          <p className="chart-source">{source}</p>
        </div>
      </div>
      <div className="scroll-scenes">
        <Scrollama offset={0.8} onStepEnter={handleStepEnter}>
          {scenes.map((scene, i) => {
            return (
              <Step data={scene} index={i} key={`scene-${i}`}>
                <div
                  className="scene"
                  style={{ visibility: scene.text ? "visible" : "hidden" }}
                >
                  <div className="article-wrapper scroller-text">
                    <p dangerouslySetInnerHTML={createMarkup(scene.text)}></p>
                  </div>
                </div>
              </Step>
            );
          })}
        </Scrollama>
      </div>
    </div>
  );
};

export default Scroller;
